import withStyles from 'react-jss';
import { StylesProps } from '../../theme/jss-types';
import React from 'react';
import {
    Card, Checkbox, Dropdown, Spin, Button,
    Menu, Tag,
} from 'antd';
import Paginator from 'components/common/paginator';
import moment from 'moment';
import StatusPrettyName from './status-pretty-name';
import SingleMonthRangePicker from './DatePickerMobile';
import { STATUS_COLOR_MAP } from './consignments.helper';
import { CaretDownOutlined } from '@ant-design/icons';

const styles = () => ({
    main: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    scrollContainer: {
        flexGrow: 1,
        overflowY: 'auto',
        position: 'relative',
    },
    card: {
        margin: '6px',
        borderRadius: '8px !important',
    },
    cardContent: {
        color: '#444',
        display: 'flex',
    },
    cardContentRight: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        fontSize: '12px',
    },
    checkboxContainer: {
        marginRight: 8,
        display: 'flex',
        alignItems: 'center',
    },
    checkbox: {
        '& .ant-checkbox .ant-checkbox-inner': {
            borderRadius: 15,
        },
        '& .ant-checkbox-checked .ant-checkbox-inner': {
            borderRadius: 15,
        },
    },
    paginationGroup: {
        direction: 'ltr',
        display: 'flex',
        fontSize: 12,
        height: '35px',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        marginTop: '5px',
    },
    spinWrapper: {
        width: '100%',
        height: '100%',
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    actionButton: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '12px',
        height: '40px',
        padding: '8px',
        backgroundColor: '#fff',
    },
    resetFilter: {
        color: '#DF1D1D',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        marginLeft: 16,
        fontFamily: 'Open Sans',
    },
    filterButton: {
        paddingLeft: 8,
        paddingRight: 8,
        borderRadius: '6px !important',
    },
    filters: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: 8,
        borderBottom: '1px solid #eee',
        flexDirection: 'column',
    },
    noData: {
        width: '100%',
        textAlign: 'center',
        color: '#888',
        padding: '32px 0',
        fontSize: 16,
    },
    dateFilter: {
        padding: 12,
        background: '#fff',
        borderRadius: 4,
        margin: '5px 10px',
    },
    statusFilter: {
        padding: 12,
        background: '#fff',
        borderRadius: 4,
        minWidth: 220,
        margin: '5px 10px',
    },
});

interface ConsignmentPageMobileProps extends StylesProps<ReturnType<typeof styles>> {
    consignments: any;
    consignmentsSelected?: string[];
    setConsignmentSelected?: (selected: string[]) => void;
    handlePagination: (action: 'next' | 'prev' | 'first') => void;
    pagination: {
        currentPageNumber: number;
        isNextPresent: boolean;
    };
    loading?: boolean;
    actionMenu: any;
    renderActions: any;
    setMoreFiltersVisible: any;
    handleResetFilters: any;
    renderSearch: () => React.ReactNode;
    useMariaDb?: boolean;
    handleDateChange?: any;
    filters: any;
    onStatusChange: any;
    statusOptions: any;
    goToDetailsPage: (cn: any) => void;
    isMoreFilterAdded?: any;
}

const ConsignmentPageMobileComponent = (props: ConsignmentPageMobileProps) => {
    const {
        consignments,
        classes,
        consignmentsSelected = [],
        setConsignmentSelected,
        pagination,
        handlePagination,
        loading,
        actionMenu,
        renderActions,
        setMoreFiltersVisible,
        handleResetFilters,
        renderSearch,
        useMariaDb,
        handleDateChange,
        filters,
        statusOptions,
        onStatusChange,
        goToDetailsPage,
        isMoreFilterAdded,
    } = props;

    const [selectedCn, setSelectedCn] = React.useState(null as any);
    const [dateDropdownVisible, setDateDropdownVisible] = React.useState(false);
    const [statusDropdownVisible, setStatusDropdownVisible] = React.useState(false);
    const [statusValue, setStatusValue] = React.useState<any[]>([]);
    const [actionsDropdownVisible, setActionsDropdownVisible] = React.useState(false);

    React.useEffect(() => {
        if (typeof filters.statusList === 'string' && filters.statusList.length > 0) {
            setStatusValue(filters.statusList.split(',').map((s: string) => s.trim()).filter(Boolean));
        } else {
            setStatusValue([]);
        }
    }, [filters.statusList]);

    React.useEffect(() => {
        if (consignmentsSelected.length) {
            setSelectedCn(consignments.find((cn: any) => cn.reference_number === consignmentsSelected[0]));
        } else {
            setSelectedCn(null);
        }
    }, [consignmentsSelected]);


    const handleSelect = (item: any) => {
        if (setConsignmentSelected) {
            const idx = consignmentsSelected.indexOf(item.reference_number);
            let newSelected: string[];
            if (idx > -1) {
                newSelected = consignmentsSelected.filter((ref) => ref !== item.reference_number);
            } else {
                newSelected = [...consignmentsSelected, item.reference_number];
            }
            setConsignmentSelected(newSelected);
        }
    };

    const handleDateApply = (dates: [moment.Moment | null, moment.Moment | null]) => {
        setDateDropdownVisible(false);
        if (handleDateChange) {
            handleDateChange(dates);
        }
    };

    const handleStatusTagToggle = (val: string) => {
        setStatusValue((prev) => {
            if (prev.includes(val)) {
                return [...prev.filter((v) => v !== val)];
            }
            return [...prev, val];
        });
    };

    const handleStatusApply = () => {
        setStatusDropdownVisible(false);
        onStatusChange(statusValue.length ? statusValue.join(',') : undefined);
    };

    const handleStatusClear = () => {
        setStatusValue([]);
        onStatusChange(undefined);
        setStatusDropdownVisible(false);
    };

    const renderDateFilter = () => {
        return (
            <Dropdown
                visible={dateDropdownVisible}
                onVisibleChange={setDateDropdownVisible}
                trigger={['click']}
                overlay={(
                    <div className={classes.dateFilter}>
                        <SingleMonthRangePicker
                            onApply={handleDateApply}
                            initialValue={[moment(filters.startDate), moment(filters.endDate)]}
                        />
                    </div>
                )}
                placement="bottomLeft"
            >
                <Button
                    className={classes.filterButton}
                >
                    Date Range
                    <CaretDownOutlined />
                </Button>
            </Dropdown>
        );
    };

    const renderStatusFilter = () => {
        const statusFilterApplied = statusValue.length > 0;
        return (
            <Dropdown
                visible={statusDropdownVisible}
                onVisibleChange={setStatusDropdownVisible}
                trigger={['click']}
                overlay={(
                    <div className={classes.statusFilter}>
                        <div style={{
                            display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12,
                        }}
                        >
                            {Array.isArray(statusOptions) && statusOptions.map((opt: any) => {
                                return (
                                    <Tag.CheckableTag
                                        key={opt.value}
                                        checked={statusValue.includes(opt.value)}
                                        onChange={() => handleStatusTagToggle(opt.value)}
                                        style={{
                                            padding: '4px 12px',
                                            borderRadius: 16,
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            border: statusValue.includes(opt.value)
                                                ? '1.5px solid #1677ff'
                                                : '1px solid #d9d9d9',
                                            background: statusValue.includes(opt.value) ? '#e6f4ff' : '#fff',
                                            color: statusValue.includes(opt.value) ? '#1677ff' : '#595959',
                                            fontWeight: statusValue.includes(opt.value) ? 600 : 400,
                                        }}
                                    >
                                        <StatusPrettyName status={opt.value} />
                                    </Tag.CheckableTag>
                                );
                            })}
                        </div>
                        <div style={{
                            display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center',
                        }}
                        >
                            <div
                                onClick={handleStatusClear}
                                style={{
                                    cursor: 'pointer',
                                    color: 'red',
                                }}
                            >
                                Clear
                            </div>
                            <Button
                                type="primary"
                                onClick={handleStatusApply}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                )}
                placement="bottomLeft"
            >
                <Button
                    className={classes.filterButton}
                    style={{
                        color: statusFilterApplied ? '#006ec3' : '#111111',
                        border: statusFilterApplied ? '1px solid #006ec3' : '1px solid #d9d9d9',
                        background: statusFilterApplied ? '#e6f4ff' : '#fff',
                    }}
                >
                    Status
                    <CaretDownOutlined />
                </Button>
            </Dropdown>
        );
    };

    const renderResetFiltersOption = () => {
        const filterApplied = isMoreFilterAdded();
        if (!filterApplied) {
            return null;
        }
        return (
            <div
                className={classes.resetFilter}
                onClick={() => handleResetFilters()}
            >
                Clear
            </div>
        );
    };

    const renderMoreFiltersOption = () => {
        const filterApplied = isMoreFilterAdded(true);
        return (
            <Button
                onClick={() => setMoreFiltersVisible(true)}
                style={{
                    color: filterApplied ? '#006ec3' : '#111111',
                    border: filterApplied ? '1px solid #006ec3' : '1px solid #d9d9d9',
                    backgroundColor: filterApplied ? '#e6f4ff' : '#fff',
                    borderRadius: '6px',
                }}
            >
                Filters
            </Button>
        );
    };

    const renderFilters = () => (
        <div>
            <div
                className={classes.filters}
            >
                <div style={{ width: '100%' }}>
                    {renderSearch()}
                </div>
                <div style={{
                    display: 'flex', gap: 8, width: '100%', marginTop: 4, alignItems: 'center',
                }}
                >
                    {renderMoreFiltersOption()}
                    {renderStatusFilter()}
                    {renderDateFilter()}
                    {renderResetFiltersOption()}
                </div>
            </div>
        </div>
    );

    const handleActionsButtonClick = () => {
        // Always close first, then open on next tick to force dropdown to open
        setActionsDropdownVisible(false);
        setTimeout(() => setActionsDropdownVisible(true), 0);
    };

    const renderActionsOptions = () => {
        const singleMenu = selectedCn ? actionMenu(selectedCn) : null;
        const bulkMenu = renderActions();
        return (
            <Dropdown
                visible={actionsDropdownVisible}
                onVisibleChange={setActionsDropdownVisible}
                overlay={
                    (
                        <Menu
                            onClick={() => setActionsDropdownVisible(false)}
                        >
                            {consignmentsSelected.length === 1 && singleMenu}
                            {consignmentsSelected.length === 1 && <Menu.Divider />}
                            {bulkMenu}
                        </Menu>
                    )
                }
                placement="topRight"
                trigger={['click']}
            >
                <Button type="primary" onClick={handleActionsButtonClick}>Actions</Button>
            </Dropdown>
        );
    };

    const renderPaginator = () => {
        return (
            <div className={classes.paginationGroup}>
                <Paginator
                    currentPageNumber={pagination.currentPageNumber}
                    isNextPresent={pagination.isNextPresent}
                    onNextClick={() => handlePagination('next')}
                    onPrevClick={() => handlePagination('prev')}
                    onFirstClick={() => handlePagination('first')}
                    hideFirstButton
                    leftText="Previous"
                    rightText="Next"
                    style={{
                        justifyContent: 'space-between',
                        width: '100%',
                        height: '100%',
                        margin: '0px',
                        backgroundColor: '#fff',
                        padding: '0px 8px',
                    }}
                    showPageNumberText
                />
            </div>
        );
    };

    if (loading) {
        return (
            <div className={classes.spinWrapper}>
                <Spin spinning={loading} />
            </div>
        );
    }

    return (
        <div className={classes.main}>
            <div>
                {renderFilters()}
            </div>
            <div className={classes.scrollContainer}>
                {Array.isArray(consignments) && consignments.length === 0 ? (
                    <div className={classes.noData}>
                        No data found
                    </div>
                ) : (
                    (Array.isArray(consignments) ? consignments : []).map((item: any) => {
                        const statusKey = String(item.status).toLowerCase();
                        const statusColorObj = STATUS_COLOR_MAP[statusKey] || {
                            color: '#595959',
                            background: '#fafafa',
                        };
                        return (
                            <Card
                                key={item.reference_number}
                                className={classes.card}
                                size="small"
                            >
                                <div className={classes.cardContent}>
                                    <div className={classes.checkboxContainer}>
                                        <Checkbox
                                            className={classes.checkbox}
                                            checked={consignmentsSelected.includes(item.reference_number)}
                                            onChange={() => handleSelect(item)}
                                        />
                                    </div>
                                    <div className={classes.cardContentRight}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <div
                                                style={{ fontWeight: 700, fontSize: 14 }}
                                                onClick={() => goToDetailsPage(item)}
                                            >
                                                {item.reference_number}
                                            </div>
                                            <div
                                                style={{
                                                    color: statusColorObj.color,
                                                    background: statusColorObj.background,
                                                    borderRadius: 6,
                                                    padding: '2px 10px',
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                    minWidth: 70,
                                                    textAlign: 'center',
                                                    display: 'inline-block',
                                                }}
                                            >
                                                <StatusPrettyName status={item.status} useMariaDb={useMariaDb} />
                                            </div>
                                        </div>
                                        <div>
                                            Expected:
                                            {' '}
                                            <span style={{ fontWeight: 700 }}>
                                                {item.expected_delivery_date
                                                    ? moment(item.expected_delivery_date).format('DD MMM, YYYY') : 'NA'}
                                            </span>
                                        </div>
                                        <div>
                                            To:
                                            {' '}
                                            <span style={{ fontWeight: 700 }}>{item.destination_name}</span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        );
                    })
                )}
            </div>
            {consignmentsSelected.length > 0 && (
                <div className={classes.actionButton}>
                    <div>
                        {consignmentsSelected.length}
                        {' '}
                        selected
                    </div>
                    {renderActionsOptions()}
                </div>
            )}
            <div>
                {renderPaginator()}
            </div>
        </div>
    );
};

const ConsignmentPageMobile = withStyles(styles)(ConsignmentPageMobileComponent);
export default ConsignmentPageMobile;
